import { track } from '@eventbrite/datalayer-library';
import { ProgressIndicator } from '@eventbrite/eds-progress-indicator';
import { getWindowObject } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { LoadingButton } from '@eventbrite/marmalade';
import loadable from '@loadable/component';
import React, { useEffect, useMemo, useState } from 'react';
import { LISTING_HEAP_IDS } from '../../../../constants';
import {
    useEventBasicInformationContext,
    useTicketContext,
} from '../../../../contexts';
import { useListingTimedEntryExperimentContext } from '../../../../contexts/ListingTimedEntryExperimentContext';
import {
    LocalizedDateTime,
    SALES_STATUS,
    UNDETERMINED_SALES_STATUSES,
    type CheckoutWidgetProps,
    type ChildEventProps,
    type ServerEvent,
} from '../../../../types';
import {
    eventIsOnSale,
    eventIsSalesEnded,
    eventIsSoldOut,
} from '../../../../utils';
import { getLocalizedFormattedPriceString } from '../../../../utils/priceFormat';
import { getFormattedDateCard } from '../../../utils/localizedDates';
import { useTimedEntryCalendarData } from '../../WhenAndWhere/DateAndTime/useTimedEntryCalendarData';
import { ExploreSimilarEventsButton } from '../ExploreSimilarEventsButton/ExploreSimilarEventsButton';
import './CheckAvailability.scss';
import { buildCheckoutUrl, getCheckAvailabilityWidgetEventId } from './utils';

interface CheckAvailabilityProps
    extends CheckoutWidgetProps,
        Pick<ServerEvent, 'isPreview'> {
    eventStartDate?: LocalizedDateTime;
    eventEndDate?: LocalizedDateTime;
    onConfirm: () => void;
}

interface PanelDisplayMessageProps {
    locale: string;
    parentEventId?: string;
    eventStartDate?: LocalizedDateTime;
    eventEndDate?: LocalizedDateTime;
    isSoldOut?: boolean;
    isSalesEndedEvent?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = (): void => {};

const getValidChildEvents = (childEvents: Array<ChildEventProps>) => {
    return childEvents.filter(
        (event) =>
            eventIsOnSale(event) ||
            UNDETERMINED_SALES_STATUSES.includes(
                event?.sales_status as SALES_STATUS,
            ) ||
            eventIsSalesEnded(event) ||
            eventIsSoldOut(event),
    );
};

const filterUnavailableEventDates = (childEvents: Array<ChildEventProps>) => {
    return childEvents.filter((event) => {
        if (!event.availability) {
            return false;
        }
        return (
            event.availability.toLowerCase() === 'soldout' ||
            event.availability.toLowerCase() === 'salesended'
        );
    });
};

const getPanelDisplayMessage = ({
    locale,
    eventStartDate,
    eventEndDate,
    parentEventId,
    isSoldOut,
    isSalesEndedEvent,
}: PanelDisplayMessageProps) => {
    if (isSalesEndedEvent) {
        return gettext('Sales Ended');
    }
    if (isSoldOut) {
        return gettext('Sold Out');
    }
    if (!locale || !eventStartDate || !eventEndDate) {
        return '';
    }

    const eventStartDateInfo = getFormattedDateCard({
        date: eventStartDate,
        locale: locale,
    });

    if (parentEventId) {
        return gettext('Multiple dates');
    } else {
        return `${eventStartDateInfo.monthAbbr} ${eventStartDateInfo.day}`;
    }
};

export const getFormattedPriceDetails = (ticketData: any, locale: string) => {
    const minimumTicketPrice = ticketData?.availability?.minimumTicketPrice;
    const eventIsFree = ticketData?.availability?.isFree;
    if (!minimumTicketPrice || eventIsFree) {
        return ticketData?.panelDisplayPrice;
    }

    const { currency, value } = minimumTicketPrice;

    if (currency == null || value == null) {
        return ticketData?.panelDisplayPrice;
    }

    const formattedPrice = getLocalizedFormattedPriceString({
        locale,
        currency,
        value: value,
    });

    return gettext('From %(price)s', {
        price: formattedPrice,
    });
};

export const CheckAvailability: React.FC<CheckAvailabilityProps> = ({
    eventStartDate,
    eventEndDate,
    onConfirm,
    isPreview,
    affiliateCode,
    campaign_token,
    rsvpToken,
    waitlistToken,
    promoCode,
}) => {
    const { isListingTimedEntryExperimentActive } =
        useListingTimedEntryExperimentContext();
    const [isLoading, setIsLoading] = useState(true);
    const basicEventInformation = useEventBasicInformationContext();
    const modalTriggerElementId = useMemo(
        () => `check-availability-btn-${basicEventInformation.parentEventId}`,
        [basicEventInformation.parentEventId],
    );
    const [moreOptionsModalOpened, setMoreOptionsModalOpened] = useState(false);
    const { ticketData, isFetchComplete } = useTicketContext();
    const { fetchTimedEntryCalendarData, loading } =
        useTimedEntryCalendarData();

    const LazyMoreOptionsModal = useMemo(
        () =>
            loadable(
                () =>
                    import(
                        '../../WhenAndWhere/ChildEventsSelection/MoreOptionsModal/MoreOptionsModal'
                    ),
            ),
        [],
    );

    useEffect(() => {
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createCheckoutWidget = (eventId: string | undefined) => {
        const ebWidgets = getWindowObject('EBWidgets');
        if (!ebWidgets || !eventId) {
            return false;
        }

        getWindowObject('EBWidgets')?.createWidget({
            widgetType: 'checkout',
            eventId: eventId,
            modal: true,
            modalTriggerElementId,
            ...(!isPreview ? { affiliateCode } : {}),
            ...(waitlistToken ? { waitlistToken } : {}),
            ...(promoCode ? { promoCode: escape(promoCode) } : {}),
            ...(rsvpToken ? { rsvpToken } : {}),
            ...(campaign_token ? { campaign_token } : {}),
        });
        return true;
    };
    useEffect(() => {
        if (!isFetchComplete || !isListingTimedEntryExperimentActive) return;

        const eventIdToUse = getCheckAvailabilityWidgetEventId({
            waitlistToken,
            basicEventInformation,
        });
        // Try to create widget immediately
        const created = createCheckoutWidget(eventIdToUse);
        // If SDK not ready, retry after a delay (to avoid race conditions with Core Legacy Logic - EB-317602)
        if (!created) {
            const timeoutId = setTimeout(() => {
                createCheckoutWidget(eventIdToUse);
            }, 1000);
            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isFetchComplete,
        modalTriggerElementId,
        basicEventInformation.parentEventId,
    ]);

    if (!isFetchComplete) {
        return (
            <div className="express-checkout-progressbar">
                <ProgressIndicator style="gradient" size="medium-chunky" />
            </div>
        );
    }

    const childEventsToShow =
        basicEventInformation.currentEventSessionsTimedEntry;

    const handleMoreOptionsClick = async () => {
        setMoreOptionsModalOpened(true);
        const startDateTime =
            basicEventInformation?.start?.local ??
            childEventsToShow?.[0]?.start?.local;

        if (startDateTime) {
            await fetchTimedEntryCalendarData(startDateTime);
        }
    };

    const validChildEvents = getValidChildEvents(
        basicEventInformation.childEvents ?? [],
    );

    const unavailableEvents = filterUnavailableEventDates(
        basicEventInformation.childEvents ?? [],
    );

    const panelDisplayMessage = getPanelDisplayMessage({
        locale: basicEventInformation.locale || '',
        parentEventId: basicEventInformation.parentEventId,
        eventStartDate: basicEventInformation.parentEventId
            ? eventStartDate
            : basicEventInformation.start,
        eventEndDate: basicEventInformation.parentEventId
            ? eventEndDate
            : basicEventInformation.end,
        isSoldOut: basicEventInformation.isSoldOut,
        isSalesEndedEvent: basicEventInformation.isSalesEndedEvent,
    });

    const formattedPriceDetails = getFormattedPriceDetails(
        ticketData,
        basicEventInformation.locale || 'en-US',
    );

    const handleClick = () => {
        track({
            eventName: LISTING_HEAP_IDS.CHECK_AVAILABILITY_CLICK,
            eventData: {},
        });
        if (isListingTimedEntryExperimentActive) {
            return;
        }
        handleMoreOptionsClick();
    };
    const handleClickWhenLoaded = (e: any) => {
        // Only prevent default if we're sure the SDK will handle it
        if (getWindowObject('EBWidgets')) {
            e.preventDefault();
            handleClick?.();
        }
    };

    const showExploreSimilarEventsButton =
        basicEventInformation.isSoldOut ||
        basicEventInformation.isSalesEndedEvent;

    return (
        <section>
            <div className="check-availability-btn check-availability-btn--checkout-opener">
                <div className="check-availability-btn__body">
                    <div className="check-availability-btn__panel-info">
                        {!showExploreSimilarEventsButton &&
                            formattedPriceDetails}
                        {panelDisplayMessage && (
                            <div
                                className={
                                    showExploreSimilarEventsButton
                                        ? 'check-availability-btn__panel-sold-out-message'
                                        : 'check-availability-btn__panel-message'
                                }
                            >
                                {panelDisplayMessage}
                            </div>
                        )}
                    </div>
                </div>
                <div className="check-availability-btn__cta-wrapper">
                    {showExploreSimilarEventsButton ? (
                        <ExploreSimilarEventsButton />
                    ) : (
                        // Having a link + button allow us to define a fallback behavior (open checkout-external) under low connection / No JS available scenarios (EB-315757)
                        // Once the checkout modal is ready, we won't do the fallback behavior
                        <a
                            target="_blank"
                            href={buildCheckoutUrl(
                                basicEventInformation.serverUrl,
                                basicEventInformation.parentEventId || '', // as this is an edge case solution, we use the parent event id, as the link is not considering the waitlist token checked on getCheckAvailabilityWidgetEventId
                                promoCode,
                                waitlistToken,
                                campaign_token,
                            )}
                            rel="noopener noreferrer"
                            onClick={!isLoading ? handleClickWhenLoaded : noop}
                        >
                            <LoadingButton
                                id={modalTriggerElementId}
                                isLoading={isLoading}
                                onClick={handleClick}
                                variant="primary"
                                className="check-availability-btn__button"
                            >
                                {gettext('Check availability')}
                            </LoadingButton>
                        </a>
                    )}
                </div>
            </div>
            {moreOptionsModalOpened && (
                <LazyMoreOptionsModal
                    onClose={() => setMoreOptionsModalOpened(false)}
                    onConfirm={onConfirm}
                    opened={moreOptionsModalOpened}
                    childEvents={validChildEvents}
                    currentEventStartDate={basicEventInformation?.start}
                    currentEventId={basicEventInformation.id}
                    isTimedEntryEnabled={true}
                    soldOutEvents={unavailableEvents}
                    isLoading={loading}
                />
            )}
        </section>
    );
};
